'use client';

import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import Link from 'app-migration/components/link/Link';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

import { noop } from 'utils/noop';

import { Icon, IconName } from '../Icon';
import Text from '../Text';

const CircularProgress = dynamic(() => import('@material-ui/core/CircularProgress'));

export type ButtonRank = 'primary' | 'secondary' | 'tertiary';
type ButtonSize = 'massive' | 'medium' | 'small' | 'tiny';
type FontSize = 'medium' | 'small' | 'heading';
type ButtonVariant = 'default' | 'yes' | 'no' | 'sell' | 'destructive' | 'inverse' | 'brand' | 'pill' | 'neutral';

interface BaseButtonProps {
    text: JSX.Element | string;
    textColor?: string;
    icon?: IconName;
    iconColor?: string;
    trailingIcon?: IconName;
    rank?: ButtonRank;
    size?: ButtonSize;
    fontSize?: FontSize;
    fullWidth?: boolean;
    variant?: ButtonVariant;
    disabled?: boolean;
    loading?: boolean;
    image?: JSX.Element;
    onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
    className?: string;
}
interface LinkButtonProps extends BaseButtonProps {
    href: string;
    external?: boolean;
    newTab?: boolean;
    buttonProps?: never;
    anchorProps?: AnchorHTMLAttributes<HTMLAnchorElement>;
    forceNext?: boolean;
}
interface ButtonButtonProps extends BaseButtonProps {
    href?: never;
    external?: never;
    newTab?: never;
    buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
    anchorProps?: never;
    forceNext?: never;
}

type ButtonProps = LinkButtonProps | ButtonButtonProps;

const Button = ({
    text,
    textColor,
    icon,
    iconColor,
    trailingIcon,
    rank = 'primary',
    size = 'medium',
    fontSize = 'medium',
    fullWidth = false,
    loading = false,
    disabled = false,
    variant = 'default',
    onClick = noop,
    href,
    external,
    newTab = false,
    image,
    forceNext,
    buttonProps,
    anchorProps,
    className,
}: ButtonProps): JSX.Element => {
    const ChosenIcon = icon && Icon[icon];
    const iconElement = ChosenIcon && <ChosenIcon size={'20px'} color={iconColor} />;

    const ChosenTrailingIcon = trailingIcon && Icon[trailingIcon];
    const trailingIconElement = ChosenTrailingIcon && <ChosenTrailingIcon size={'20px'} />;

    const SizedText =
        fontSize === 'small'
            ? Text.Body.x10
            : fontSize === 'heading'
            ? Text.Heading.x10
            : size === 'medium'
            ? Text.Emphasis.x30
            : Text.Emphasis.x20;
    const textElement = <SizedText color={textColor}>{text}</SizedText>;

    // Base button styles
    const baseStyles =
        'inline-flex justify-center items-center rounded-x30 box-border cursor-pointer border-none h-fit whitespace-nowrap flex-nowrap';

    // Size styles
    const sizeStyles = {
        primary: {
            massive: 'py-2 px-1 min-w-[160px] min-h-[48px]',
            medium: 'py-1.5 px-2 min-w-[72px] min-h-[48px]',
            small: 'py-1 px-1.5 min-w-[60px] min-h-[40px]',
            tiny: 'py-0.5 px-1.5 min-w-[50px] min-h-[32px]',
        },
        tertiary: {
            massive: 'py-1.5 px-2 min-h-[48px]',
            medium: 'py-1.5 px-1 min-h-[48px]',
            small: 'py-1 px-1 min-h-[40px]',
            tiny: 'py-1 px-1 min-h-[32px]',
        },
    };

    // Rank and variant styles
    const rankVariantStyles = {
        primary: {
            default: 'text-text-inverse bg-fill-x10 active:bg-fill-x30',
            yes: 'bg-yes-x10 text-text-inverse active:bg-yes-x50',
            no: 'bg-no-x10 text-text-inverse active:bg-no-x50',
            sell: 'bg-sell-x10 text-text-inverse active:bg-sell-x40',
            destructive: 'bg-red-x10 text-text-inverse active:bg-red-x50',
            inverse: 'bg-fill-inverse text-text-x10 active:opacity-80',
            pill: 'bg-fill-x50 text-text-x10 active:opacity-80',
            brand: 'bg-brand-primary text-text-inverse active:bg-green-x50',
            neutral: 'bg-[#6a71ee] text-text-inverse active:bg-[#4a51ce]',
        },
        secondary: {
            default:
                'border !border-solid border-fill-x40 bg-custom-transparent text-text-x10 hover:bg-fill-x50 active:bg-fill-x30',
            yes: 'border !border-solid border-fill-x40 bg-custom-transparent text-yes-x10 hover:bg-yes-x20 active:bg-yes-x30',
            no: 'border !border-solid border-fill-x40 bg-custom-transparent text-no-x10 hover:bg-no-x20 active:bg-no-x30',
            sell: 'border !border-solid border-sell-x50 bg-custom-transparent text-text-x10 hover:bg-sell-x20 active:bg-sell-x30',
            destructive:
                'border !border-solid border-fill-x40 bg-custom-transparent text-red-x10 hover:bg-red-x20 active:bg-red-x30',
            inverse:
                'border !border-solid border-fill-inverse bg-custom-transparent text-text-inverse hover:opacity-80 active:opacity-60',
            pill: 'border !border-solid border-fill-x50 bg-custom-transparent text-text-inverse hover:opacity-80 active:opacity-60',
            brand: 'border !border-solid border-fill-x40 bg-custom-transparent text-green-x10 hover:bg-green-x20 active:bg-green-x30',
            neutral:
                'border !border-solid border-[#6a71ee] bg-custom-transparent text-[#6a71ee] hover:bg-[#6a71ee]/10 active:bg-[#6a71ee]/20',
        },
        tertiary: {
            default: 'bg-[#00000000] text-text-x10 border-none hover:bg-fill-x50 active:bg-fill-x30',
            yes: 'bg-[#00000000] text-yes-x10 hover:bg-yes-x20 active:bg-yes-x30',
            no: 'bg-[#00000000] text-no-x10 hover:bg-no-x20 active:bg-no-x30',
            sell: 'bg-[#00000000] text-text-x10 hover:bg-sell-x20 active:bg-sell-x30',
            destructive: 'bg-[#00000000] text-red-x10 hover:bg-red-x20 active:bg-red-x30',
            inverse: 'bg-[#00000000] text-text-inverse hover:opacity-80 active:opacity-60',
            pill: 'bg-[#00000000] text-text-inverse hover:opacity-80 active:opacity-60',
            brand: 'bg-[#00000000] text-green-x10 border-none hover:bg-fill-x50 active:bg-green-x30',
            neutral: 'bg-[#00000000] text-[#6a71ee] hover:bg-[#6a71ee]/10 active:bg-[#6a71ee]/20',
        },
    };

    // Disabled styles
    const disabledStyles = {
        primary: {
            default: 'bg-fill-x10 text-text-inverse !cursor-not-allowed opacity-20',
            yes: 'bg-yes-x10 text-text-inverse !cursor-not-allowed opacity-20',
            no: 'bg-no-x10 text-text-inverse !cursor-not-allowed opacity-20',
            sell: 'bg-fill-x10 text-text-inverse !cursor-not-allowed opacity-20',
            brand: 'bg-brand-primary text-text-inverse !cursor-not-allowed opacity-20',
            neutral: 'bg-[#6a71ee] text-text-inverse !cursor-not-allowed opacity-20',
        },
        secondary: 'border !border-solid bg-custom-transparent text-text-x30 !cursor-not-allowed',
        tertiary: 'bg-custom-transparent text-text-x30 !cursor-not-allowed',
        secondaryBrand: 'bg-custom-transparent text-green-x10 !cursor-not-allowed opacity-20',
    };

    // Loading styles
    const loadingStyles = 'relative';
    const loadingIconStyles = 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';

    // Combine styles
    const selectedStyles = clsx(
        baseStyles,
        !disabled &&
            'transition-[opacity,background-color,transform] duration-83 hover:opacity-80 active:scale-90 active:transition-[opacity,background-color,transform] active:duration-50 [&:not(:active)]:transition-[opacity,background-color,transform] [&:not(:active)]:duration-[83ms,83ms,167ms] outline-none outline-offset-0 outline-0 outline-[#00000000] focus-visible:outline-2 focus-visible:outline-brand-primary',
        fullWidth ? 'w-full' : '',
        rank === 'tertiary' ? sizeStyles.tertiary[size] : sizeStyles.primary[size],
        loading ? loadingStyles : '',
        rankVariantStyles[rank][variant],
        disabled && rank === 'primary' && variant === 'default'
            ? disabledStyles.primary.default
            : disabled && rank === 'primary' && variant === 'yes'
            ? disabledStyles.primary.yes
            : disabled && rank === 'primary' && variant === 'no'
            ? disabledStyles.primary.no
            : disabled && rank === 'primary' && variant === 'sell'
            ? disabledStyles.primary.sell
            : disabled && rank === 'primary' && variant === 'brand'
            ? disabledStyles.primary.brand
            : disabled && rank === 'primary' && variant === 'neutral'
            ? disabledStyles.primary.neutral
            : disabled && rank === 'secondary' && variant === 'brand'
            ? disabledStyles.secondaryBrand
            : disabled && disabledStyles[rank],
        className,
    );

    const content = (
        <>
            {iconElement}
            {iconElement && <span className={'inline-block w-2'}></span>}
            {textElement}
            {trailingIconElement && <span className={'inline-block w-2'}></span>}
            {trailingIconElement}
            {image && <span className={'inline-block w-2'}></span>}
            {image}
        </>
    );

    const loadingIcon = (
        <span className={`${loadingIconStyles} button-loading-icon`}>
            <CircularProgress size={'24px'} variant={'indeterminate'} color={'inherit'} />
        </span>
    );

    const contentOrLoadingIcon = loading ? loadingIcon : content;

    // Pull out width so the wrapper matches child width (if defined)
    const {
        style: {
            backgroundColor = undefined,
            color = undefined,
            padding = undefined,
            border = undefined,
            borderRadius = undefined,
            borderWidth = undefined,
            boxShadow = undefined,
            ...restStyle
        } = {},
        ...restButtonProps
    } = buttonProps || {};

    return !!external && !!href ? (
        <a
            href={href}
            className={selectedStyles}
            style={{ fontFamily: 'var(--inter-font)' }}
            {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : undefined)}
            onClick={disabled ? undefined : (onClick as () => void)}
            {...anchorProps}
        >
            {contentOrLoadingIcon}
        </a>
    ) : !!href ? (
        <Link
            onClick={disabled ? undefined : onClick}
            to={href}
            {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : undefined)}
            rel={newTab ? 'noopener noreferrer' : undefined}
            target={newTab ? '_blank' : undefined}
            className={selectedStyles}
            style={{ fontFamily: 'var(--inter-font)' }}
            forceNext={forceNext}
            {...anchorProps}
        >
            {contentOrLoadingIcon}
        </Link>
    ) : (
        <div
            onClick={disabled || loading ? undefined : onClick}
            className={fullWidth ? 'w-full' : ''}
            style={{ ...restStyle }}
        >
            <button
                disabled={disabled || loading}
                className={selectedStyles + ' w-full'}
                style={{
                    fontFamily: 'var(--inter-font)',
                    backgroundColor,
                    color,
                    padding,
                    border,
                    borderRadius,
                    borderWidth,
                    boxShadow,
                }}
                {...restButtonProps}
            >
                {contentOrLoadingIcon}
            </button>
        </div>
    );
};

export default Button;
